import { useRef, useState } from 'react'
import { ArrowsPointingOutIcon, CheckIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { AdManagementService, type BrandProduct } from '../../client'

const TILE = 'group relative overflow-hidden rounded-lg border transition'
const TILE_ON = 'border-purple-900 ring-2 ring-purple-200'
const TILE_OFF = 'border-slate-200 hover:border-slate-300'
const CORNER =
  'absolute flex size-6 items-center justify-center rounded-full bg-black/70 opacity-0 transition group-hover:opacity-100 disabled:opacity-40'

type Props = {
  brandId: number
  products: BrandProduct[]
  selectedIds: string[]
  companySelected: boolean
  brandName: string
  brandFavicon: string | null
  maxSelected: number
  loading: boolean
  onToggleProduct: (product: BrandProduct) => void
  onToggleCompany: () => void
  onProductsChange: (products: BrandProduct[]) => void
  onPreview: (url: string) => void
  onClose: () => void
}

export default function ProductPicker({
  brandId,
  products,
  selectedIds,
  companySelected,
  brandName,
  brandFavicon,
  maxSelected,
  loading,
  onToggleProduct,
  onToggleCompany,
  onProductsChange,
  onPreview,
  onClose,
}: Props) {
  const [uploading, setUploading] = useState(false)
  const [removing, setRemoving] = useState<string[]>([])
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const atLimit = selectedIds.length >= maxSelected

  async function upload(file: File) {
    setUploading(true)
    try {
      const created = await AdManagementService.createProduct(brandId, { file })
      onProductsChange([...products, created])
    } catch {
      setError('Failed to upload product image')
    } finally {
      setUploading(false)
    }
  }

  async function remove(productId: string) {
    setRemoving((current) => [...current, productId])
    try {
      await AdManagementService.deleteProduct(productId, brandId)
      onProductsChange(products.filter(({ id }) => id !== productId))
    } catch {
      setError('Failed to remove product')
    } finally {
      setRemoving((current) => current.filter((id) => id !== productId))
    }
  }

  return (
    <div className="w-full rounded-lg bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-700">Select a product</p>
        <button type="button" aria-label="Close" onClick={onClose} className="text-slate-400 hover:text-slate-600">
          <XMarkIcon className="size-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <span className="inline-block size-5 animate-spin rounded-full border-2 border-slate-200 border-t-purple-600" />
        </div>
      ) : (
        <>
          <div className="grid max-h-64 grid-cols-3 gap-2 overflow-y-auto pr-1 [scrollbar-width:thin]">
            <button
              type="button"
              disabled={selectedIds.length > 0}
              title={selectedIds.length > 0 ? 'Deselect products to use your brand' : undefined}
              onClick={onToggleCompany}
              className={`${TILE} ${companySelected ? TILE_ON : TILE_OFF} disabled:cursor-not-allowed disabled:opacity-40`}
            >
              <span className="flex aspect-square items-center justify-center bg-purple-50 p-2">
                {brandFavicon ? (
                  <img src={brandFavicon} alt="" referrerPolicy="no-referrer" className="size-10 object-contain" />
                ) : (
                  <span className="flex size-10 items-center justify-center rounded-full bg-purple-200 text-sm font-semibold text-purple-900">
                    {(brandName[0] ?? '?').toUpperCase()}
                  </span>
                )}
              </span>
              <span className="block truncate px-1 py-1 text-center text-[10px] text-slate-600">
                {brandName || 'Company'}
              </span>
              {companySelected && (
                <span className="pointer-events-none absolute top-1 left-1 flex size-4 items-center justify-center rounded-full bg-purple-900">
                  <CheckIcon className="size-2.5 text-white" strokeWidth={3} />
                </span>
              )}
            </button>

            {products.map((product) => {
              const selected = selectedIds.includes(product.id)
              const disabled = companySelected || (!selected && atLimit)

              return (
                <div key={product.id} className={`${TILE} ${selected ? TILE_ON : TILE_OFF}`}>
                  <button
                    type="button"
                    disabled={disabled}
                    title={
                      companySelected
                        ? 'Deselect your brand to pick products'
                        : disabled
                          ? `Up to ${maxSelected} products`
                          : product.label
                    }
                    onClick={() => onToggleProduct(product)}
                    className="block w-full disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <span className="block aspect-square bg-slate-100">
                      {product.image_urls?.[0] && (
                        <img src={product.image_urls[0]} alt={product.label} className="size-full object-cover" />
                      )}
                    </span>
                    <span className="block truncate px-1 py-1 text-[10px] text-slate-600">{product.label}</span>
                  </button>

                  {selected && (
                    <span className="pointer-events-none absolute top-1 left-1 flex size-4 items-center justify-center rounded-full bg-purple-900">
                      <CheckIcon className="size-2.5 text-white" strokeWidth={3} />
                    </span>
                  )}

                  <button
                    type="button"
                    aria-label={`Remove ${product.label}`}
                    disabled={removing.includes(product.id)}
                    onClick={() => void remove(product.id)}
                    className={`${CORNER} top-1 left-1`}
                  >
                    <XMarkIcon className="size-3.5 text-white" />
                  </button>
                  {product.image_urls?.[0] && (
                    <button
                      type="button"
                      aria-label={`Expand ${product.label}`}
                      onClick={() => onPreview(product.image_urls?.[0] ?? '')}
                      className={`${CORNER} top-1 right-1`}
                    >
                      <ArrowsPointingOutIcon className="size-3 text-white" />
                    </button>
                  )}
                </div>
              )
            })}
          </div>

          <div className="mt-3 flex items-center justify-between gap-2">
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 text-xs text-slate-500 transition hover:text-slate-700 disabled:cursor-default"
            >
              {uploading ? (
                <>
                  <span className="inline-block size-3.5 animate-spin rounded-full border-2 border-slate-200 border-t-purple-600" />
                  Uploading…
                </>
              ) : (
                <>
                  <PlusIcon className="size-4" />
                  Add product
                </>
              )}
            </button>
            <p className="text-[10px] text-slate-400">
              {selectedIds.length}/{maxSelected} selected
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0]
              event.target.value = ''
              if (file) void upload(file)
            }}
          />
        </>
      )}

      {error && <p className="mt-2 text-[11px] text-red-500">{error}</p>}
    </div>
  )
}
